import React, { FC, RefObject, useCallback, useContext, useEffect, useState } from "react";
import "./EditTemplate.scss";
import { useParams } from "react-router-dom";
import ApiContext from "../../../../Api/ApiContext";
import { useEventSubscriber } from "../../../../Api/ApiEvents/ApiEventSubscriber";
import { useTranslation } from "react-i18next";
import { UploadTemplate } from "../CreateTemplate/CreateTemplate";
import { IClause, ITemplate } from "../../Interfaces";
import Clauses from "../../Components/Clauses/Clauses";
import { ReactSVG } from "react-svg";
import listIcon from "../../../../Assets/icons/list-bullets.svg";
import messageIcon from '../../../../Assets/icons/chat-circle-text.svg';
import Grid from "../../Components/Grid/Grid";
import MultiSwitch from "../../../../Components/MultiSwitch/MultiSwitch";
import Copilot from "../../../Copilot/Copilot";
import DraftingHeader, { EDraftingHeaderSource } from "../../Components/Header/DraftingHeader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../Store/store";
import { draftingSlice } from "../../../../Store/draftingSlice";
import Loading from "../../../../Components/Loading/Loading";
import NoDataScreen, { ENDSType } from "../../Components/NoDataScreen/NoDataScreen";
import { selectConfig } from "../../../../Store/configSlice";
import Modal from "../../../../Components/Modal/Modal";
import Button, { EButtonSize } from "../../../../Components/Buttons/Button";

const EditTemplate: FC = () => {
    const { id } = useParams();
    
    const [ template, setTemplate ] = useState<ITemplate>(null);
    const [ clauses, setClauses ] = useState<IClause[]>([]);
    const [ status, setStatus ] = useState(null);

    const api = useContext(ApiContext);
    const { t } = useTranslation();

    const fetchTemplate = useCallback(() => {
        api.get('/drafts/templates/' + id).then(res => {
            setTemplate(res.data.template);
            setClauses(res.data.clauses);
            setStatus(res.data.template.status);
        });
    }, [id, api]);

    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        dispatch(draftingSlice.actions.setRedraftingClause(null));
        fetchTemplate();
    }, [fetchTemplate, dispatch]);

    useEventSubscriber("template-status", template?.id, (status: string) => {
        if(status === "ready") {
            fetchTemplate();
        }
    });

    const [ tab, setTab ] = useState(0);

    const addNewClauseAtSequence = (sequence: number) => {
        const seq = sequence + 1;
        const newClause: IClause = {
            id: null,
            template_id: template.id,
            content: t('drafting.newClauseContent'),
            sequence: seq,
            title: t('drafting.newClauseTitle')
        };

        setClauses([
            ...clauses.slice(0, seq),
            newClause,
            ...clauses.slice(seq)
        ]);

        api.post(`/drafts/templates/${template.id}/clauses`, {
            content: newClause.content,
            sequence: newClause.sequence,
            title: newClause.title
        }).then(() => {
            fetchTemplate();
        });
    }

    const updateClause = (clause: IClause) => {
        api.put(`/drafts/templates/${template.id}/clauses/${clause.id}`, {
            content: clause.content,
            sequence: clause.sequence,
            title: clause.title
        }).then(() => {
            fetchTemplate();
        });
    }

    const updateClauseBySequence = (sequence: number, clause: IClause) => {
        const index = clauses.findIndex(c => c.sequence === sequence);
        setClauses([
            ...clauses.slice(0, index),
            clause,
            ...clauses.slice(index + 1)
        ]);

        const instructions = clause.instructions && clause.instructions.length === 1 && clause.instructions[0] === "" ? null : clause.instructions;

        api.put(`/drafts/templates/${template.id}/clauses/${clause.id}`, {
            content: clause.content,
            sequence: clause.sequence,
            title: clause.title,
            instructions
        })
    }

    const updateTemplateSequence = (result: any) => {
        if (!result.destination) {
            return;
        }
    
        const updatedItems = [...clauses];
        const [reorderedItem] = updatedItems.splice(result.source.index, 1);
        updatedItems.splice(result.destination.index, 0, reorderedItem);
    
        setClauses(updatedItems);

        const seqOrder = updatedItems.map((clause, index) => ({ clauseId: clause.id, sequence: index }));
        api.put(`drafts/templates/${template.id}/clauses/sequence`, {
            order: [ ...seqOrder ]
        }).then(() => {
            fetchTemplate();
        });
    }

    const updateTemplateName = (name: string) => api.put(`/drafts/templates/${template.id}`, { name }).then(() => fetchTemplate());

    const deleteClauseById = (clauseId: number) => {
        const updatedClauses = clauses.filter(clause => clause.id !== clauseId);
        setClauses(updatedClauses);
        api.delete(`/drafts/templates/${template.id}/clauses/${clauseId}`).then(() => {
            fetchTemplate();
        });
    }

    const [ nameModalOpen, setNameModalOpen ] = useState(false);

    const [ name, setName ] = useState(template?.name);

    useEffect(() => {

        setName(template?.name);

        if(template) {
            setNameModalOpen(template?.name === 'New Template');
        }
    }, [template]);

    const config = useSelector(selectConfig);

    if(!template) return <Loading />

    if(status !== "published" && !config.admin) {
        return <NoDataScreen type={ENDSType.NO_TEMPLATE_CONTENT} />
    }

    if(status === "queued") return <UploadTemplate template={template} />

    const { type, jurisdiction } = template;

    const ChooseNameModal = () => {

        const [localName, setLocalName] = useState("");

        const inputRef: RefObject<HTMLInputElement> = React.createRef();

        useEffect(() => {
            inputRef.current.focus();
        }, [name]);

        return (
            <div className="namepicker">
                <input 
                    type="text"
                    ref={inputRef}
                    placeholder={t('drafting.chooseName')}
                    value={localName} 
                    onChange={(e) => setLocalName(e.target.value)} 
                />
                <Button 
                    text={t('save')}
                    size={EButtonSize.NORMAL}
                    onClick={() => {
                        updateTemplateName(localName);
                        setNameModalOpen(false);
                    }}
                />
            </div>
        )
    }

    return (
        <>
            <Modal
                showModal={nameModalOpen}
                setShowModal={setNameModalOpen}
                title={t('drafting.chooseName')}
                content={<ChooseNameModal />} 
            />
            <DraftingHeader
                id={id}
                name={name}
                type={type}
                jurisdiction={jurisdiction}
                source={EDraftingHeaderSource.TEMPLATE}
                updateName={updateTemplateName}/>
            <Clauses
                clauses={clauses}
                documentId={template.id}
                setClauses={setClauses}
                updateSequence={updateTemplateSequence}
                addNewClauseAtSequence={addNewClauseAtSequence}
                updateClauseBySequence={updateClauseBySequence}
                deleteClauseById={deleteClauseById}
            />
        </>
    )
}

export default EditTemplate;